import React from "react";
import { MessageCircle, Mail } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { buildWhatsAppLink, buildMailLink, renderTemplate } from "@/lib/cta";
import { trackCta } from "@/lib/analytics";

const handleAnchor = (e, href) => {
    if (href && href.startsWith("#")) {
        e.preventDefault();
        const el = document.getElementById(href.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

export const Footer = () => {
    const { content } = useContent();
    const c = content.contact;
    const f = content.footer;
    const whatsappHref = buildWhatsAppLink(c.whatsapp_number, renderTemplate(c.whatsapp_template, { area: "" }));
    const mailHref = buildMailLink(c.email, c.email_subject, renderTemplate(c.email_template, { area: "" }));

    return (
        <footer data-testid="site-footer" className="bg-[#050E1E] text-slate-400 pt-16 pb-10 border-t border-slate-800/60">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-2.5">
                            <span className="inline-flex items-center justify-center w-9 h-9 bg-white text-[#0A192F] font-display font-semibold">A</span>
                            <span className="font-display font-semibold text-white text-lg">Aurea Digital Solutions</span>
                        </div>
                        <p className="mt-4 text-[13.5px] leading-relaxed text-slate-400 max-w-md">{f.tagline}</p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <a
                                href={whatsappHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-whatsapp-cta"
                                onClick={() => trackCta("whatsapp_click", { source: "footer" })}
                                className="btn-base btn-primary !text-[13px] !py-2.5 !px-4"
                            >
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp
                            </a>
                            <a
                                href={mailHref}
                                data-testid="footer-email-cta"
                                onClick={() => trackCta("email_click", { source: "footer" })}
                                className="btn-base !text-[13px] !py-2.5 !px-4 border border-white/20 text-white hover:bg-white/10"
                            >
                                <Mail className="w-4 h-4" />
                                Email
                            </a>
                        </div>
                    </div>

                    {(f.groups || []).map((group) => (
                        <div key={group.heading} className="lg:col-span-2">
                            <h4 className="font-display text-[12px] uppercase tracking-[0.22em] text-teal-300 font-semibold">{group.heading}</h4>
                            <ul className="mt-5 space-y-3">
                                {(group.links || []).map((link) => (
                                    <li key={link.label}>
                                        <a
                                            href={link.href}
                                            target={link.external ? "_blank" : undefined}
                                            rel={link.external ? "noopener noreferrer" : undefined}
                                            onClick={(e) => {
                                                if (link.external && link.label === "AWSAI Diagnostic") {
                                                    trackCta("awsai_click", { source: "footer_link" });
                                                } else {
                                                    handleAnchor(e, link.href);
                                                }
                                            }}
                                            className="text-[13.5px] text-slate-400 hover:text-white transition-colors"
                                        >
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="lg:col-span-3">
                        <h4 className="font-display text-[12px] uppercase tracking-[0.22em] text-teal-300 font-semibold">Contact</h4>
                        <ul className="mt-5 space-y-3 text-[13.5px]">
                            <li>
                                <p className="text-slate-500 text-[11.5px] uppercase tracking-[0.15em]">Email</p>
                                <a
                                    href={mailHref}
                                    className="text-slate-200 hover:text-teal-300 transition-colors break-all"
                                    data-testid="footer-email-link"
                                    onClick={() => trackCta("email_click", { source: "footer_contact_link" })}
                                >
                                    {c.email}
                                </a>
                            </li>
                            <li>
                                <p className="text-slate-500 text-[11.5px] uppercase tracking-[0.15em]">WhatsApp</p>
                                <a
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-slate-200 hover:text-teal-300 transition-colors"
                                    data-testid="footer-whatsapp-link"
                                    onClick={() => trackCta("whatsapp_click", { source: "footer_contact_link" })}
                                >
                                    {c.whatsapp_display}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-[12.5px] text-slate-500">© {new Date().getFullYear()} {f.copyright_suffix}</p>
                    <p className="text-[12px] text-slate-500">{f.credit_line}</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
